import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { useTranslation } from 'react-i18next';
import '../../styles/exports/exports.scss';
import ExportButton from '../../components/export/exportButton';
import { WeezHeader, Message, Card, Icon, ToastContext, ToggleLabel } from '@weezevent/nacre';
import EventModel from '../../models/eventModel';
import Style from './exports.module.css';
import LoadingCard from '../../components/loadingCard';
import { getMenhirUrl } from '../../utils/config';

function ExportItem({ icon, title, subtitle, cta }) {
    return (
        <div className={Style['export-container']}>
            <div className={Style['export-left']}>
                <div className={Style['export-icon']}>
                    <Icon name={icon} margin={false} />
                </div>
                <div className={Style['export-title-container']}>
                    <div className={Style['export-title']}>{title}</div>
                    <div className={Style['export-subtitle']}>{subtitle}</div>
                </div>
            </div>
            <div className={Style['export-right']}>{cta}</div>
        </div>
    );
}
export default function Exports({ organization }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [exportSessionFilters, setExportSessionFilters] = React.useState('event_status=published,draft');

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false
        },
        [organization]
    );

    const menhirUrl = getMenhirUrl();

    return (
        <>
            <WeezHeader title={t('ticket.statistics.exports.pageTitle')} />
            <LoadingCard loading={loadingEvents}>
                {menhirUrl && (
                    <Message
                        type="default"
                        title={t('ticket.statistics.exports.message-title')}
                        links={{
                            label: t('ticket.statistics.exports.message-cta'),
                            href: `${menhirUrl}/bo/distributors/export`
                        }}
                    >
                        {t('ticket.statistics.exports.message-content')}
                    </Message>
                )}
                <Card>
                    <Card.Content className={Style['card-export-container']}>
                        <ExportItem
                            icon="users"
                            title={t('ticket.export.export_title_attendee')}
                            subtitle={t('ticket.export.export_description_attendee')}
                            cta={
                                <ExportButton
                                    events={events}
                                    organization={organization}
                                    choiceProcess={'attendees'}
                                    enableEventsFilter
                                    withModal={true}
                                    showButton={true}
                                    onError={() => toast.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.common.action.download')}
                                    modalBodyTrans={t('ticket.export.export_description_attendee')}
                                    modalTitleTrans={t('ticket.export.export_title_attendee')}
                                    datesInQueryFilters={true}
                                    datesFormat={{
                                        sold_start_datetime: date => moment(date).format('YYYY-MM-DD'),
                                        sold_end_datetime: date => moment(date).format('YYYY-MM-DD')
                                    }}
                                />
                            }
                        />
                        <ExportItem
                            icon="cart"
                            title={t('ticket.export.export_title_order')}
                            subtitle={t('ticket.export.export_description_order')}
                            cta={
                                <ExportButton
                                    events={events}
                                    organization={organization}
                                    choiceProcess={'orders_excel'}
                                    withModal={true}
                                    showButton={true}
                                    onError={() => toast.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.common.action.download')}
                                    modalBodyTrans={t('ticket.export.export_description_order')}
                                    modalTitleTrans={t('ticket.export.export_title_order')}
                                    datesFormat={{
                                        sold_start_datetime: date => moment.utc(date),
                                        sold_end_datetime: date => moment.utc(date)
                                    }}
                                />
                            }
                        />
                        <ExportItem
                            icon="tickets"
                            title={t('ticket.export.export_title_rate')}
                            subtitle={t('ticket.export.export_description_rate')}
                            cta={
                                <ExportButton
                                    events={events}
                                    organization={organization}
                                    choiceProcess={'count_attendees_by_rate_excel'}
                                    withModal={true}
                                    showButton={true}
                                    onError={() => toast.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.common.action.download')}
                                    modalBodyTrans={t('ticket.export.export_description_rate')}
                                    modalTitleTrans={t('ticket.export.export_title_rate')}
                                    datesFormat={{
                                        sold_start_datetime: date => moment.utc(date),
                                        sold_end_datetime: date => moment.utc(date)
                                    }}
                                />
                            }
                        />
                        <ExportItem
                            icon="calendar-star"
                            title={t('ticket.export.export_title_event')}
                            subtitle={t('ticket.export.export_description_event')}
                            cta={
                                <ExportButton
                                    events={events}
                                    organization={organization}
                                    choiceProcess={'events'}
                                    enableEventsFilter
                                    withModal={true}
                                    showButton={true}
                                    onError={() => toast.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.common.action.download')}
                                    modalBodyTrans={t('ticket.export.export_description_event')}
                                    modalTitleTrans={t('ticket.export.export_title_event')}
                                    datesInQueryFilters={true}
                                    datesFormat={{
                                        sold_start_datetime: date => moment(date).format('YYYY-MM-DD'),
                                        sold_end_datetime: date => moment(date).format('YYYY-MM-DD')
                                    }}
                                />
                            }
                        />
                        <ExportItem
                            icon="time"
                            title={t('ticket.export.export_title_session')}
                            subtitle={t('ticket.export.export_description_session')}
                            cta={
                                <ExportButton
                                    withCustomizableModal
                                    queryFilters={exportSessionFilters}
                                    customSubtitle={t('ticket.export.sessions.modal-body')}
                                    customTitle={t('ticket.export.sessions.modal-title')}
                                    events={events}
                                    organization={organization}
                                    choiceProcess={'sessions'}
                                    showButton={true}
                                    onError={() => toast.error(t('ticket.export.download_state_error'))}
                                    buttonTrans={t('ticket.common.action.download')}
                                    modalContent={
                                        <div className={Style['toggle-container']}>
                                            <ToggleLabel
                                                inlinePosition="left"
                                                label={t('ticket.export.sessions.toggle-label')}
                                                onChange={(e, { checked }) => {
                                                    if (checked) {
                                                        setExportSessionFilters('event_status=published,draft,closed');
                                                    }
                                                }}
                                            />
                                        </div>
                                    }
                                />
                            }
                        />
                    </Card.Content>
                </Card>
            </LoadingCard>
        </>
    );
}

Exports.propTypes = {
    organization: PropTypes.object.isRequired
};
